import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Table, Spinner, Alert, Button, Select, Label } from "flowbite-react";
import { getAuthToken } from "../../api/CapitalAuth";
import { debounce } from "lodash";
import { capitalApiClient } from "../../api/CapitalApiClient";

const API_URLS = {
  LIVE: "https://api-capital.backend-capital.com/api/v1",
  DEMO: "https://demo-api-capital.backend-capital.com/api/v1",
};

const OpenPositions = ({ apiKey, identifier, password, onPositionClick }) => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedApiUrl, setSelectedApiUrl] = useState(API_URLS.LIVE);

  const fetchOpenPositions = useCallback(
    async (retryCount = 1) => {
      console.log("Fetching open positions...");
      setLoading(true);
      setError(null);
      try {
        // Initialize the client with the current selectedApiUrl
        capitalApiClient.init(apiKey, identifier, password, selectedApiUrl);
        const response = await capitalApiClient.request("GET", "/positions");
        setPositions(response.positions || []);
      } catch (err) {
        console.error("Error fetching open positions:", err);
        if (err.response && (err.response.status === 429 || err.response.status === 500)) {
          if (retryCount < 3) {
            const delay = Math.pow(2, retryCount) * 1000;
            console.log(`Error ${err.response.status}. Retrying in ${delay}ms...`);
            setTimeout(() => fetchOpenPositions(retryCount + 1), delay);
            return; // Exit the function here to prevent setting error state
          } else {
            setError(`${err.response.status === 429 ? "Rate limit exceeded" : "Server error"}. Please try again later.`);
          }
        } else {
          setError(err.response?.data?.errorMessage || err.message || "An error occurred while fetching open positions");
        }
      } finally {
        setLoading(false);
      }
    },
    [apiKey, identifier, password, selectedApiUrl]
  );

  const debouncedFetchOpenPositions = useCallback(debounce(fetchOpenPositions, 1000), [fetchOpenPositions]);

  useEffect(() => {
    if (apiKey && identifier && password) {
      debouncedFetchOpenPositions();
    }
    return () => debouncedFetchOpenPositions.cancel();
  }, [debouncedFetchOpenPositions, selectedApiUrl]);

  useEffect(() => {
    if (apiKey && identifier && password) {
      debouncedFetchOpenPositions();
    }
    return () => debouncedFetchOpenPositions.cancel();
  }, [debouncedFetchOpenPositions, apiKey, identifier, password]);

  const handleApiUrlChange = (e) => {
    const newApiUrl = e.target.value;
    setSelectedApiUrl(newApiUrl);
    // Fetch new positions immediately when URL changes
    debouncedFetchOpenPositions.cancel();
    fetchOpenPositions();
  };

  if (loading) {
    return <Spinner aria-label="Loading open positions" />;
  }

  if (error) {
    return (
      <Alert color="failure">
        {error}
        <Button onClick={() => fetchOpenPositions()} className="mt-2">
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <div className="p-4 mb-4 rounded-lg dark:bg-slate-800 text-slate-400 bg-white">
      <h3 className="text-xl font-bold mb-2 text-slate-800 dark:text-slate-200">Open Positions</h3>
      <div className="mt-8">
        <div className="mb-4 flex items-center">
          <Label htmlFor="apiUrlSelect" className="mr-2">
            API Environment:
          </Label>
          <Select id="apiUrlSelect" value={selectedApiUrl} onChange={handleApiUrlChange}>
            <option value={API_URLS.LIVE}>Live</option>
            <option value={API_URLS.DEMO}>Demo</option>
          </Select>
        </div>
        {positions.length === 0 ? (
          <p>No open positions found.</p>
        ) : (
          <Table striped>
            <Table.Head>
              <Table.HeadCell>Instrument</Table.HeadCell>
              <Table.HeadCell>Direction</Table.HeadCell>
              <Table.HeadCell>Size</Table.HeadCell>
              <Table.HeadCell>Open Level</Table.HeadCell>
              <Table.HeadCell>Current Level</Table.HeadCell>
              <Table.HeadCell>Profit/Loss</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {positions.map((position) => (
                <Table.Row
                  key={position?.position?.dealId || Math.random()}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => onPositionClick(position?.market?.epic)}
                >
                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {position?.market?.instrumentName || "N/A"}
                  </Table.Cell>
                  <Table.Cell>{position?.position?.direction || "N/A"}</Table.Cell>
                  <Table.Cell>{position?.position?.size || "N/A"}</Table.Cell>
                  <Table.Cell>{position?.position?.level || "N/A"}</Table.Cell>
                  <Table.Cell>{position?.market?.bid || "N/A"}</Table.Cell>
                  <Table.Cell
                    className={
                      position?.position?.upl >= 0
                        ? "text-green-500"
                        : position?.position?.upl < 0
                        ? "text-red-500"
                        : "text-gray-500"
                    }
                  >
                    {position?.position?.upl !== undefined ? position.position.upl.toFixed(2) : "N/A"}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>
    </div>
  );
};

export default OpenPositions;
